import {useState, useCallback, useEffect} from 'react';

import {ContentRequester} from '../../Domain/Groups/ContentRequester';

const FETCH_ALL = 10000;

const useSingleProjectTree = (workspace, projectId) => {
    const [loadedChildren, setChildrenCache] = useState({});
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [loadingNodes, setLoadingNodes] = useState(new Set());

    const loadChildren = useCallback(async (nodeId, nodeType, nestedRoute) => {
        setLoadingNodes((prev) => new Set(prev).add(nodeId));
        let result = null;
        try {
            result = await ContentRequester.getChildrenFoldersAndProcedures(workspace, {
                parentId: nodeId, parentType: nodeType, nestedRoute,
                skip: 0, limit: FETCH_ALL, includePublishedRoles: true,
            });
            setChildrenCache((prev) => ({...prev, [nodeId]: result}));
        } catch (error) {
            console.error('Failed to load children:', error);
        }
        setLoadingNodes((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
        });
        return result;
    }, [workspace]);

    const handleExpand = useCallback(async (nodeId, nodeType, nestedRoute) => {
        const wasExpanded = expandedNodes.has(nodeId);
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            wasExpanded ? next.delete(nodeId) : next.add(nodeId);
            return next;
        });
        if (!wasExpanded && !loadedChildren[nodeId]) {
            await loadChildren(nodeId, nodeType, nestedRoute);
        }
    }, [expandedNodes, loadedChildren, loadChildren]);

    useEffect(() => {
        if (!projectId || loadedChildren[projectId]) return;
        loadChildren(projectId, 'project', projectId);
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const bulkSetChildren = useCallback((entries) => {
        setChildrenCache((prev) => {
            const next = {...prev};
            for (const [key, value] of Object.entries(entries)) {
                if (!next[key]) next[key] = value;
            }
            return next;
        });
    }, []);

    return {loadedChildren, expandedNodes, loadingNodes, handleExpand, loadChildren, bulkSetChildren};
};

export default useSingleProjectTree;
